/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Create your first sync", description = "Learn the steps to creating a Hightouch sync and follow along to run your first one.", lastUpdatedDate = new Date(1687996800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Create your first sync",
        description: "Learn the steps to creating a Hightouch sync and follow along to run your first one.",
        lastUpdatedDate: "Thu Jun 29 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Connect a source",
          "depth": 0,
          "id": "connect-a-source"
        }, {
          "value": "Set up a model",
          "depth": 0,
          "id": "set-up-a-model"
        }, {
          "value": "Connect a destination",
          "depth": 0,
          "id": "connect-a-destination"
        }, {
          "value": "Configure a sync",
          "depth": 0,
          "id": "configure-a-sync"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      ol: "ol",
      li: "li",
      a: "a",
      strong: "strong",
      h2: "h2"
    }, _provideComponents(), props.components), {DemoEmbed} = _components;
    if (!DemoEmbed) _missingMdxReference("DemoEmbed", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Running your first sync is satisfying—you can see your source data appear in the tools and apps your organization lives in. To get there, you need to set a few things up first."
      }), "\n", _jsx(_components.p, {
        children: "It's best to follow this setup sequence when creating your first sync:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#connect-a-source",
            children: _jsx(_components.strong, {
              children: "Connect a source"
            })
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#set-up-a-model",
            children: _jsx(_components.strong, {
              children: "Set up a model"
            })
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#connect-a-destination",
            children: _jsx(_components.strong, {
              children: "Connect a destination"
            })
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#configure-a-sync",
            children: _jsx(_components.strong, {
              children: "Configure a sync"
            })
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can learn the specifics of each step by clicking on the section link. Or check out the ", _jsx(_components.a, {
          href: "/getting-started/concepts",
          children: "core concepts"
        }), " to review Hightouch's fundamentals."]
      }), "\n", _jsx(DemoEmbed, {
        title: "Take an interactive tour of Hightouch",
        description: "This interactive tour guides you through creating a Snowflake source, Salesforce destination, and a sync to upsert contacts and leads to Salesforce. It assumes you've already configured a model."
      }), "\n", _jsx(_components.h2, {
        id: "connect-a-source",
        children: "Connect a source"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.a, {
          href: "/getting-started/concepts#sources",
          children: "source"
        }), " is where your organization's business data lives. To add a new source to your Hightouch workspace, follow these steps:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/sources",
            children: [_jsx(_components.strong, {
              children: "Sources"
            }), " overview page"]
          }), " and click the ", _jsx(_components.strong, {
            children: "Add source"
          }), " button."]
        }), "\n", _jsx(_components.li, {
          children: "Select the source you want to add."
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If you want to test things out without connecting a production source, you can\nuse the B2B SaaS or B2C e-commerce ", _jsx(_components.strong, {
            children: "Sample datasets"
          }), ". They're available at\nthe end of the source list."]
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsx(_components.li, {
          children: "Enter the relevant credentials. These differ depending on the source. Refer to the relevant source documentation for further details."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), ". Before continuing, Hightouch validates that you have the necessary access and permissions. If the test fails, you need to confirm and re-enter your credentials."]
        }), "\n", _jsxs(_components.li, {
          children: ["Give your source a ", _jsx(_components.strong, {
            children: "Source name"
          }), ". This name is how your source appears in the Hightouch workspace. It helps to include details about the sources' environment, data contents, and owners, particularly if you plan on connecting multiple instances of the same source, for example, a development and prod version."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've finished, your source appears on the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and you can use it set up models."]
      }), "\n", _jsx(_components.h2, {
        id: "set-up-a-model",
        children: "Set up a model"
      }), "\n", _jsx(_components.p, {
        children: "Models define what data to pull from your source. To set up a new model, follow these steps:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/models",
            children: [_jsx(_components.strong, {
              children: "Models"
            }), " overview page"]
          }), " and click ", _jsx(_components.strong, {
            children: "Add model"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select a source you've already connected."
        }), "\n", _jsxs(_components.li, {
          children: ["Choose a ", _jsx(_components.a, {
            href: "/models/creating-models",
            children: "modeling method"
          }), ": SQL editor, table selector, dbt model, or Looker Look selector. If you're using one of the Sample dataset sources, the SQL editor comes with pre-populated SQL queries. You can also use the table selector with the Sample datasets."]
        }), "\n", _jsxs(_components.li, {
          children: ["Before continuing, you must ", _jsx(_components.strong, {
            children: "Preview"
          }), " your model to ensure it's querying the data you're interested in. By default, the preview displays the first 100 records. Once you've validated your data, click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Give your model a ", _jsx(_components.strong, {
            children: "Model name"
          }), ". It's best to include details about the model's contents and business purpose so you can easily identify it. Including more information is beneficial if you plan on using your model for multiple syncs or having many team members in your workspace."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select a ", _jsx(_components.strong, {
            children: "Primary key"
          }), ". A primary key should be a column with unique identifiers. A column with unique values is essential for Hightouch to ", _jsx(_components.a, {
            href: "/getting-started/concepts#change-data-capture",
            children: "sync data efficiently"
          }), ". Click ", _jsx(_components.strong, {
            children: "Finish"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've finished, your model appears on the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/models",
          children: [_jsx(_components.strong, {
            children: "Models"
          }), " overview page"]
        }), " and you can use it to set up syncs."]
      }), "\n", _jsx(_components.p, {
        children: "To set up a sync, you need to connect a destination."
      }), "\n", _jsx(_components.h2, {
        id: "connect-a-destination",
        children: "Connect a destination"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/getting-started/concepts#destinations",
          children: "Destinations"
        }), " are the tools and services you want to receive data from your source. You need to connect your destination to Hightouch before you can set up the sync configuration to the destination."]
      }), "\n", _jsx(_components.p, {
        children: "To set up a new destination, follow these steps:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/destinations",
            children: [_jsx(_components.strong, {
              children: "Destinations"
            }), " overview page"]
          }), " and click the ", _jsx(_components.strong, {
            children: "Add destination"
          }), " button."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the destination you want to add and click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If you want to test things out without connecting a production destination,\nyou can use the ", _jsx(_components.a, {
            href: "/destinations/google-sheets",
            children: "Google Sheets"
          }), " destination.\nGoogle Sheets is a ", _jsx(_components.a, {
            href: "/pricing/ss-pricing#lite-destinations",
            children: "lite destination"
          }), "\nand straightfoward to set up."]
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsx(_components.li, {
          children: "Authorize connecting to your destination or enter the relevant credentials and inputs. These differ depending on the destination. Refer to the relevant destination documentation for further details."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), ". Before continuing, Hightouch validates that you have the necessary access and permissions. If the test fails, you need to confirm and re-enter your credentials."]
        }), "\n", _jsxs(_components.li, {
          children: ["Give your destination a ", _jsx(_components.strong, {
            children: "Destination name"
          }), ". This name is how your destination appears in the Hightouch workspace. It helps to include details about the destination's business purposes and owners, particularly if you plan on connecting multiple instances of the destination, for example, a development and prod version."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've finished, your destination appears on the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and you can use it configure syncs."]
      }), "\n", _jsx(_components.h2, {
        id: "configure-a-sync",
        children: "Configure a sync"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/getting-started/concepts#syncs",
          children: "Syncs"
        }), " declare how your source data should appear in your destination. To configure one, you need to have set up a source, model, and destination first."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/syncs",
            children: [_jsx(_components.strong, {
              children: "Syncs"
            }), " overview page"]
          }), " and click the ", _jsx(_components.strong, {
            children: "Add sync"
          }), " button."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the relevant model connected to the source from which you want to pull data. If you need help determining which model to choose, you can select ", _jsx(_components.strong, {
            children: "View query"
          }), " to see the model's definition."]
        }), "\n", _jsx(_components.li, {
          children: "Select the destination you want to send data to."
        }), "\n", _jsxs(_components.li, {
          children: ["Configure how you want to sync data to your destination. Configuration inputs differ depending on the destination. Refer to the relevant destination documentation for further details. Click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Set a schedule for the sync. You can learn more about scheduling options in the ", _jsx(_components.a, {
            href: "/syncs/overview#schedule",
            children: "sync overview docs"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've finished configuring a sync, you land on that sync's overview page. This page shows previous sync runs and lets you edit its ", _jsx(_components.strong, {
          children: "Configuration"
        }), ", ", _jsx(_components.a, {
          href: "/syncs/overview#schedule",
          children: _jsx(_components.strong, {
            children: "Schedule"
          })
        }), ", and set up ", _jsx(_components.a, {
          href: "/syncs/alerting",
          children: _jsx(_components.strong, {
            children: "Alerts"
          })
        }), ". It's also home to the ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/syncs/debugger",
          children: _jsx(_components.strong, {
            children: "live debugger"
          })
        }), " which helps understand previous runs down to the API request and response level. To learn more about the sync overview page, check out the ", _jsx(_components.a, {
          href: "/syncs/overview",
          children: "sync documentation"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To run the sync manually, click ", _jsx(_components.strong, {
          children: "Run"
        }), ". Check in your destination to see the fruits of your labor."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
